import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  setDirectories,
  selectDirectory,
} from "../../../state/reducers/resourcesSlice";
import axios from "axios";

const StudentResources = () => {
  const dispatch = useDispatch();
  const isSideBarOpen = useSelector((state) => state.application.isSideBarOpen);
  const directories = useSelector((state) => state.resources.directories);
  const selectedDirectory = useSelector(
    (state) => state.resources.selectedDirectory
  );
  const [previewFile, setPreviewFile] = useState(null);

  const [allResources, setAllResources] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  // Add state to track which files are currently downloading
  const [downloadingFiles, setDownloadingFiles] = useState({});

  const handleDownload = async (file) => {
    try {
      // Set downloading state for this specific file
      setDownloadingFiles((prev) => ({ ...prev, [file.name]: true }));

      const response = await axios.get(`${BASE_URL}/api/files/download/`, {
        params: {
          tab_type: "Students", // only if required by your backend
          file_path: `${selectedDirectory}/${file.name}`,
        },
        responseType: "blob", // Expect binary data
      });

      const blobUrl = URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", file.name);
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
    } catch (error) {
      console.error("Failed to download file:", error);
    } finally {
      // Reset downloading state for this file regardless of success/failure
      setDownloadingFiles((prev) => ({ ...prev, [file.name]: false }));
    }
  };

  const BASE_URL =
    process.env.REACT_APP_BASE_URL || `${process.env.REACT_APP_BASE_URL}`;

  const folders = directories.filter((item) => item.type === "folder");
  const files = directories.filter(
    (item) => item.type === "file" || item.type === "webinar"
  );

  const fetchStudentResources = async () => {
    try {
      const response = await axios.get(`${BASE_URL}/api/files/`, {
        params: {
          tab_type: "Students",
          parent_directory: selectedDirectory || "",
        },
      });
      if (response.status === 200) {
        dispatch(setDirectories(response.data));
      }
    } catch (error) {
      console.error("Failed to fetch student resources:", error);
    }
  };

  const handleBack = () => {
    dispatch(selectDirectory(""));
    setSearchTerm("");
  };

  useEffect(() => {
    fetchStudentResources();
    // eslint-disable-next-line
  }, [selectedDirectory]);

  // FILTERING based on searchTerm
  const filteredFolders = folders.filter((folder) =>
    folder.name.toLowerCase().includes(searchTerm.toLowerCase())
  );
  const filteredFiles = files.filter((file) =>
    file.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen">
      <div className="p-3 sm:p-4 md:p-6">
        <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-800 mb-4 sm:mb-6 md:mb-8">
          Resources
        </h1>

        {/* Search Bar */}
        <div className="mb-4 sm:mb-6">
          <input
            type="text"
            placeholder="Search resources..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm sm:text-base"
          />
        </div>

        {/* Back Button */}
        {selectedDirectory && selectedDirectory !== "" && (
          <button
            onClick={handleBack}
            className="mb-3 md:mb-4 px-2 py-1 sm:px-3 sm:py-2 bg-gray-300 text-gray-700 rounded text-sm sm:text-base flex items-center"
          >
            <span className="mr-1">&larr;</span> Back
          </button>
        )}

        <div className="space-y-6 md:space-y-8">
          {/* Folders Section */}
          <section>
            <h2 className="text-lg sm:text-xl font-semibold mb-3 sm:mb-4">
              Folders
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4 md:gap-6">
              {filteredFolders.length > 0 ? (
                filteredFolders.map((folder, idx) => (
                  <div
                    key={idx}
                    className="bg-white shadow-md border border-gray-200 p-3 sm:p-4 text-center cursor-pointer rounded-md hover:shadow-lg transition-shadow duration-200"
                    onClick={() => {
                      dispatch(selectDirectory(folder.name));
                      setSearchTerm("");
                    }}
                  >
                    <div className="mb-2">
                      <svg
                        className="mx-auto w-6 h-6 sm:w-8 sm:h-8 text-purple-600"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path d="M2 6a2 2 0 012-2h4l2 2h6a2 2 0 012 2v1H2V6zM2 9h16v5a2 2 0 01-2 2H2V9z" />
                      </svg>
                    </div>
                    <div className="font-medium text-sm sm:text-base truncate px-1">
                      {folder.name}
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-gray-500 col-span-1 sm:col-span-2 md:col-span-3 text-center py-6">
                  No folders found.
                </div>
              )}
            </div>
          </section>

          {/* Files/Webinars Section */}
          <section>
            <h2 className="text-lg sm:text-xl font-semibold mb-3 sm:mb-4">
              Files/Webinars
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4 md:gap-6">
              {filteredFiles.length > 0 ? (
                filteredFiles.map((file, idx) => (
                  <div
                    key={idx}
                    className="bg-white shadow-md border border-gray-200 p-3 sm:p-4 text-center rounded-md"
                  >
                    <div className="mb-2">
                      <svg
                        className="mx-auto w-6 h-6 sm:w-8 sm:h-8 text-blue-600"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path d="M17 8H3V6h14v2zm0 2v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6h14z" />
                      </svg>
                    </div>
                    <div className="font-medium text-sm sm:text-base truncate px-1 mb-2">
                      {file.name}
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDownload(file);
                      }}
                      disabled={downloadingFiles[file.name]}
                      className={`w-full mt-1 px-3 py-1.5 sm:px-4 sm:py-2 rounded ${
                        downloadingFiles[file.name]
                          ? "bg-gray-400 cursor-not-allowed"
                          : "bg-blue-500 hover:bg-blue-600"
                      } text-white text-xs sm:text-sm transition-colors duration-200`}
                    >
                      {downloadingFiles[file.name] ? (
                        <div className="flex items-center justify-center">
                          <svg
                            className="animate-spin h-4 w-4 sm:h-5 sm:w-5 mr-1 sm:mr-2 text-white"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                          <span>Downloading...</span>
                        </div>
                      ) : (
                        "Download"
                      )}
                    </button>
                  </div>
                ))
              ) : (
                <div className="text-gray-500 col-span-1 sm:col-span-2 md:col-span-3 text-center py-6">
                  No files found.
                </div>
              )}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default StudentResources;
